import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";

import { ApiError, api, setTenantId, type Me } from "../api/client";

const TENANT_STORAGE_KEY = "crabond.tenantId";

function readStoredTenant(): number | null {
  try {
    const raw = window.localStorage.getItem(TENANT_STORAGE_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function storeTenant(id: number | null): void {
  try {
    if (id === null) window.localStorage.removeItem(TENANT_STORAGE_KEY);
    else window.localStorage.setItem(TENANT_STORAGE_KEY, String(id));
  } catch {
    // storage unavailable (private mode); selection just won't persist
  }
}

/**
 * Reset every cached query except the session itself (see selectTenant/login): data goes back to
 * undefined, so mounted views show their loading state and refetch with the new tenant header
 * instead of the previous tenant's rows. (removeQueries would leave mounted observers holding
 * the old result until something re-rendered them.)
 */
export function dropTenantData(qc: QueryClient): void {
  void qc.resetQueries({ predicate: (q) => q.queryKey[0] !== "auth" });
}

export interface AuthState {
  user: Me | null;
  loading: boolean;
  /** Platform admins: tenant selected via X-Tenant-Id (null = home tenant). */
  selectedTenantId: number | null;
  selectTenant: (id: number | null) => void;
  login: (email: string, password: string) => Promise<Me>;
  logout: () => Promise<void>;
  refresh: () => Promise<unknown>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [selectedTenantId, setSelected] = useState<number | null>(() => {
    const stored = readStoredTenant();
    setTenantId(stored); // must be set before the first request goes out
    return stored;
  });

  const meQuery = useQuery({
    queryKey: ["auth", "me", selectedTenantId],
    queryFn: async () => {
      try {
        return await api.get<Me>("/api/auth/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        if (err instanceof ApiError && err.status === 404 && selectedTenantId !== null) {
          // the stored tenant no longer exists: fall back to the home tenant
          setTenantId(null);
          storeTenant(null);
          setSelected(null);
          return null;
        }
        throw err;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const selectTenant = useCallback(
    (id: number | null) => {
      setTenantId(id);
      storeTenant(id);
      setSelected(id);
      // Query keys carry no tenant, so cached rows belong to the tenant they were fetched in.
      // Invalidating would keep showing them while refetching (and keep inactive pages' rows
      // until revisited); reset them so nothing from the previous tenant is ever rendered.
      dropTenantData(qc);
    },
    [qc],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const me = await api.post<Me>("/api/auth/login", { email, password });
      setTenantId(null);
      storeTenant(null);
      setSelected(null);
      // A session can expire and another user sign in on the same tab without a logout.
      dropTenantData(qc);
      qc.setQueryData(["auth", "me", null], me);
      return me;
    },
    [qc],
  );

  const logout = useCallback(async () => {
    try {
      await api.post<void>("/api/auth/logout");
    } finally {
      // Null the session first, so the mounted observer sees "signed out". `qc.clear()` alone
      // removed the query from under it: `user` kept the old value, the login page bounced
      // straight back into the app and sign-out appeared to do nothing.
      qc.setQueriesData({ queryKey: ["auth", "me"] }, null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "auth" });
      setTenantId(null);
      storeTenant(null);
      setSelected(null);
    }
  }, [qc]);

  const value = useMemo<AuthState>(
    () => ({
      user: meQuery.data ?? null,
      loading: meQuery.isPending,
      selectedTenantId,
      selectTenant,
      login,
      logout,
      refresh: () => meQuery.refetch(),
    }),
    [meQuery, selectedTenantId, selectTenant, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Route guard: redirects to /login?next=... when signed out. */
export function RequireAuth({ children, roles }: { children: ReactNode; roles?: Me["role"][] }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <p className="p-8 text-sm text-zinc-500">Loading…</p>;
  }
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (roles && !roles.includes(user.role)) {
    return <p className="p-8 text-sm text-rose-700">You do not have access to this page.</p>;
  }
  return <>{children}</>;
}
